import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Layout } from '@/components/Layout/Layout'
import { MainHeading } from '@/components/common/MainHeading'
import { SectionCard } from '@/components/common/SectionCard'
import { DetailItem } from '@/components/common/DetailItem'
import { StatusBadge } from '@/components/common/StatusBadge'
import { NoDataFound } from '@/components/common/NoDataFound'
import Button from '@/components/common/Button'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import { callApi, ThrowError } from '@/api/callApi'
import { API_ENDPOINT, API_URL } from '@/api/constant'
import { IOrder, IOrderDetailResponse } from '@/interfaces/Order/order.interface'
import { IStore, IStoreResponse } from '@/interfaces/Store/store.interface'
import { formatDateTime, formatRand, showToastNotification, getOrderFlow, getStatusIndex, getCookies, getRole, formatTime12Hour, parseOrderAddress, formatStatus, downloadPdf } from '@/utility/helper'
import { AssignDriverModal } from '@/components/common/Model/AssignDriverModal'
import { StarRating } from '@/components/common/StarRating'
import { IReview, IReviewListResponse } from '@/interfaces/Review/review.interface'
import ViewDocumentModal, { DocumentItem } from '@/components/common/ViewDocumentModal'
import {
    CheckCircle2,
    XCircle,
    Clock,
    Truck,
    MapPin,
    Phone,
    User,
    ShoppingBag,
    ChefHat,
    Mail,
    Calendar,
    Package,
    CreditCard,
    Hash,
    Wallet,
    Tag,
    Percent,
    ShoppingCart,
    Info,
    Store,
    AlarmClock,
    Shield,
    Banknote,
    Lock,
    UserX,
    AlertCircle,
    FileText,
    Eye
} from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { usePermissions } from '@/hooks/usePermissions'
import { RESOURCES } from '@/context/permissions'
import { RootState, useAppSelector, useAppDispatch } from '@/redux/store'
import { fetchUserDetails } from '@/redux/features/UserSlice'
import { ProgressTracker } from '@/components/common/ProgressTracker'

export const OrdersView = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { isAdmin, canEdit } = usePermissions();

    const [order, setOrder] = useState<IOrder | null>(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    // Store details (Admin only)
    const [store, setStore] = useState<IStore | null>(null);

    const [cancelReason, setCancelReason] = useState("");
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelContext, setCancelContext] = useState<{ title: string; message: string }>({
        title: 'Cancel Order',
        message: 'Are you sure you want to cancel this order? This action cannot be undone.',
    });

    const { user } = useAppSelector((state: RootState) => state.user);
    const dispatch = useAppDispatch();

    const [notifyDriver, setNotifyDriver] = useState(
        user?.storeProfile?.settings?.isDriverNotificationEnabled ?? false
    );
    const [isToggleLoading, setIsToggleLoading] = useState(false);

    const handleNotificationToggle = async (checked: boolean) => {
        setNotifyDriver(checked);
        setIsToggleLoading(true);
        try {
            const formData = new FormData();
            formData.append('isDriverNotificationEnabled', String(checked));
            await callApi(
                `${API_URL}${API_ENDPOINT.updateStore}`,
                { method: 'PUT', data: formData, multipart: true }
            );
            dispatch(fetchUserDetails());
            showToastNotification('success', 'Driver notification setting updated');
        } catch (error) {
            ThrowError(error);
            // Revert state if API fails
            setNotifyDriver(!checked);
        } finally {
            setIsToggleLoading(false);
        }
    };

    // Assign Driver state
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [takeawayOtpValues, setTakeawayOtpValues] = useState<string[]>(Array(4).fill(''));

    const handleTakeawayOtpChange = (index: number, value: string) => {
        if (!/^[0-9]*$/.test(value)) return;
        const newOtp = [...takeawayOtpValues];
        newOtp[index] = value.substring(value.length - 1);
        setTakeawayOtpValues(newOtp);
        if (value && index < 3) {
            document.getElementById(`takeaway-otp-${index + 1}`)?.focus();
        }
    };

    const handleTakeawayOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !takeawayOtpValues[index] && index > 0) {
            document.getElementById(`takeaway-otp-${index - 1}`)?.focus();
        }
    };

    const handleTakeawayOtpPaste = (e: React.ClipboardEvent) => {
        const pasteData = e.clipboardData.getData('text').slice(0, 4).replace(/[^0-9]/g, '');
        if (pasteData) {
            const newOtp = [...takeawayOtpValues];
            for (let i = 0; i < pasteData.length; i++) {
                newOtp[i] = pasteData[i] || '';
            }
            setTakeawayOtpValues(newOtp);
            const nextIndex = pasteData.length < 4 ? pasteData.length : 3;
            document.getElementById(`takeaway-otp-${nextIndex}`)?.focus();
        }
    };

    const fullTakeawayOtp = takeawayOtpValues.join('');

    const [showForceDeliveryModal, setShowForceDeliveryModal] = useState(false);

    // Invoice view & download state
    const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
    const [invoiceDocItems, setInvoiceDocItems] = useState<DocumentItem[]>([]);
    const [downloadingInvoice, setDownloadingInvoice] = useState<'customer' | 'store' | null>(null);

    const handleViewInvoice = async (type: 'customer' | 'store') => {
        if (!order) return;
        setDownloadingInvoice(type);
        try {
            const url = `${API_URL}${API_ENDPOINT.orders}/${order.id}/invoice?type=${type}`;
            const invoiceTitle = `${type === 'customer' ? 'Customer' : 'Store'} Invoice #${order.orderId || order.id}`;

            const blobUrl = await downloadPdf(url);

            setInvoiceDocItems([
                {
                    id: type,
                    url: blobUrl,
                    label: invoiceTitle,
                    subtitle: `${type === 'customer' ? 'Customer' : 'Store'} Invoice`,
                    extension: 'pdf',
                }
            ]);
            setInvoiceModalOpen(true);
        } catch (error) {
            ThrowError(error);
        } finally {
            setDownloadingInvoice(null);
        }
    };


    const fetchOrder = async () => {
        if (!id) return;
        setLoading(true);
        try {
            const response = await callApi<IOrderDetailResponse>(
                `${API_URL}${API_ENDPOINT.orders}/${id}`,
                { method: 'GET' }
            );
            const orderData = response?.data || null;
            setOrder(orderData);

            // Admin: fetch store details using storeId from first item
            if (isAdmin && orderData?.store) {
                const storeId = orderData.store?.id;
                if (storeId) {
                    try {
                        const storeRes = await callApi<IStoreResponse>(
                            `${API_URL}${API_ENDPOINT.stores}/detail/${storeId}`,
                            { method: 'GET' }
                        );
                        setStore(storeRes.data || null);
                    } catch {
                        // silently ignore if store fetch fails
                        setStore(null);
                    }
                }
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrder();
    }, [id]);

    const handleStatusUpdate = async (newStatus: string, additionalPayload: any = {}) => {
        if (!order) return;
        setActionLoading(newStatus);
        try {
            const payload: any = { id: order.id, status: newStatus, ...additionalPayload };
            if (newStatus === 'cancelled' || newStatus === 'refund_rejected') {
                payload.cancelledReason = cancelReason;
            }

            await callApi(
                `${API_URL}${API_ENDPOINT.updateOrderStatus}`,
                { method: 'PATCH', data: payload }
            );
            showToastNotification('success', `Order status updated to ${formatStatus(newStatus)}`);
            fetchOrder();
        } catch (error) {
            ThrowError(error);
        } finally {
            setActionLoading(null);
            setShowCancelModal(false);
        }
    };

    // Opens confirmation modal before cancelling
    const confirmCancel = (title: string, message: string) => {
        setCancelContext({ title, message });
        setCancelReason("");
        setShowCancelModal(true);
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                </div>
            </Layout>
        );
    }

    if (!order) {
        return (
            <Layout>
                <NoDataFound
                    title="Order Not Found"
                    description="The order you are looking for might have been removed or does not exist."
                    onAction={() => navigate(-1)}
                />
            </Layout>
        );
    }

    const isCancelled = order.status === 'cancelled';
    const isFailed = order.status === 'failed';
    const showProgress = !isCancelled && !isFailed;
    const rawStatusIndex = getStatusIndex(order.status, order.driverId, order.deliveryType);
    const currentStatusIndex = rawStatusIndex >= 0 ? rawStatusIndex : 0;
    const isDriverAssignable = canEdit(RESOURCES.ORDER) &&
        ['created', 'pending', 'accepted', 'in_progress', 'ready_for_pickup'].includes(order.status) &&
        order.deliveryType !== "takeaway";
    const isDriverAssigned = Boolean(order.driver);
    const parsedAddress = parseOrderAddress(order.address);

    return (
        <Layout>
            <div className="space-y-6 pb-20 animate-in fade-in duration-500">
                <MainHeading
                    title={
                        <>
                            <Hash className="w-5 h-5 text-primary inline mr-2" />
                            {order.orderId}
                            <StatusBadge status={order.status} />
                        </>
                    }
                    description={
                        <span className="flex items-center gap-1.5">
                            <Info className="w-3.5 h-3.5" />
                            Placed on {formatDateTime(order.createdAt)}
                        </span>
                    }
                    showBack
                    onBack={() => navigate(-1)}
                    actionLabel={isDriverAssignable ? (isDriverAssigned ? "Change Driver" : "Assign Driver") : undefined}
                    actionIcon={isDriverAssignable ? <Truck className="w-4 h-4" /> : undefined}
                    onAction={() => setShowAssignModal(true)}
                />

                {/* Progress Tracker */}
                {showProgress && (
                    <ProgressTracker steps={getOrderFlow(order.driverId, order.deliveryType)} currentStatusIndex={currentStatusIndex} />
                )}

                <div className="grid grid-cols-1 items-start lg:grid-cols-3 gap-6">

                    {/* LEFT: 2-col span */}
                    <div className="lg:col-span-2 space-y-6">

                        {/* Customer Information */}
                        <SectionCard title="Customer Information" icon={<User />}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <DetailItem label="Full Name" value={order.user?.fullName || '---'} icon={<User />} />
                                <DetailItem label="Phone" value={`${order.user?.countryCode ?? ''} ${order.user?.phone ?? '---'}`} icon={<Phone />} />
                                {order.user?.email && (
                                    <DetailItem label="Email" value={order.user.email} icon={<Mail />} />
                                )}
                            </div>

                            {parsedAddress && (
                                <div className="mt-5 pt-5 border-t border-dashed">
                                    <p className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                                        <MapPin className="w-4 h-4 text-destructive" /> Delivery Address
                                    </p>
                                    <div className="bg-secondary/5 border border-secondary/10 rounded-xl p-4 space-y-1">
                                        {typeof parsedAddress === 'object' ? (
                                            <>
                                                {/* <p className="font-semibold text-sm text-foreground">
                                                    {parsedAddress.name || parsedAddress.fullName || parsedAddress.receiverName}
                                                </p> */}

                                                {/* Unit, Dept, Company, House, Building */}
                                                {[parsedAddress?.unit, parsedAddress?.department, parsedAddress?.companyName, parsedAddress?.houseNumber, parsedAddress?.buildingName].filter(Boolean).length > 0 && (
                                                    <p className="text-sm text-muted-foreground">
                                                        {[parsedAddress?.unit, parsedAddress?.department, parsedAddress?.companyName, parsedAddress?.houseNumber, parsedAddress?.buildingName].filter(Boolean).join(', ')}
                                                    </p>
                                                )}

                                                {/* Street Number & Name */}
                                                {[parsedAddress?.streetNumber, parsedAddress?.streetName].filter(Boolean).length > 0 && (
                                                    <p className="text-sm text-muted-foreground">
                                                        {[parsedAddress?.streetNumber, parsedAddress?.streetName].filter(Boolean).join(' ')}
                                                    </p>
                                                )}

                                                {/* Fallback to AddressLine 1 & 2 if standard fields are empty */}
                                                {(parsedAddress?.addressLine1 || parsedAddress?.addressLine) && (
                                                    <p className="text-sm text-muted-foreground">
                                                        {parsedAddress?.addressLine1 || parsedAddress?.addressLine}
                                                    </p>
                                                )}
                                                {parsedAddress?.addressLine2 && (
                                                    <p className="text-sm text-muted-foreground">
                                                        {parsedAddress?.addressLine2}
                                                    </p>
                                                )}

                                                {/* Suburb & City */}
                                                {[parsedAddress?.suburb, parsedAddress?.city].filter(Boolean).length > 0 && (
                                                    <p className="text-sm text-muted-foreground">
                                                        {[parsedAddress?.suburb, parsedAddress?.city].filter(Boolean).join(', ')}
                                                    </p>
                                                )}

                                                {/* Province, Postal Code, Country */}
                                                {[parsedAddress?.province, parsedAddress?.postalCode, parsedAddress?.country].filter(Boolean).length > 0 && (
                                                    <p className="text-sm text-muted-foreground">
                                                        {[parsedAddress?.province, parsedAddress?.postalCode, parsedAddress?.country].filter(Boolean).join(', ')}
                                                    </p>
                                                )}

                                                {/* Landmark */}
                                                {parsedAddress?.landmark && (
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        <span className="font-semibold">Landmark:</span> {parsedAddress?.landmark}
                                                    </p>
                                                )}

                                                {/* Phone rendering */}
                                                {(parsedAddress?.receiverName || parsedAddress?.receiverPhone || parsedAddress?.phoneNumber) && (
                                                    <div className="flex items-center gap-2 pt-2 border-t border-dashed mt-2">
                                                        <Phone className="w-3 h-3 text-muted-foreground" />
                                                        <span className="text-xs text-muted-foreground">
                                                            {[parsedAddress?.receiverName || parsedAddress?.fullName, parsedAddress?.receiverPhone || parsedAddress?.phoneNumber].filter(Boolean).join(' — ')}
                                                        </span>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <p className="text-sm text-foreground leading-relaxed">{parsedAddress}</p>
                                        )}
                                    </div>
                                    {order.deliveryInstructions && (
                                        <p className="text-xs text-muted-foreground mt-2 italic">
                                            📝 {order.deliveryInstructions}
                                        </p>
                                    )}
                                </div>
                            )}
                        </SectionCard>

                        {/* Driver Information */}
                        {order.driver && (
                            <SectionCard
                                title="Driver Information"
                                icon={<Truck />}
                                extra={
                                    isDriverAssignable ? (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setShowAssignModal(true)}
                                            leftIcon={<Truck className="w-3.5 h-3.5" />}
                                        >
                                            Change Driver
                                        </Button>
                                    ) : undefined
                                }
                            >
                                <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center mb-6 pb-6 border-b border-dashed border-border/60">
                                    <div className="w-20 h-20 rounded-2xl bg-primary/10 flex-shrink-0 flex items-center justify-center overflow-hidden border-2 border-primary/20 shadow-sm">
                                        {order.driver.media?.[0]?.url ? (
                                            <img
                                                src={API_URL + "/" + order.driver.media[0].url}
                                                alt={order.driver.fullName}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).style.display = 'none';
                                                    (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                                                }}
                                            />
                                        ) : null}
                                        <div className={`text-2xl font-black text-primary ${order?.driver?.media?.[0]?.url ? 'hidden' : ''}`}>
                                            {order?.driver?.fullName?.charAt(0)?.toUpperCase() || 'D'}
                                        </div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-xl font-black text-foreground mb-1">
                                            {order.driver.fullName}
                                        </h3>
                                        <div className="flex items-center gap-3">
                                            <p className="text-sm text-muted-foreground flex items-center gap-2">
                                                <Shield className="w-3.5 h-3.5 text-primary" />
                                                Professional Delivery Partner
                                            </p>
                                            {(() => {
                                                const driverReview = (order?.reviews || []).find(r => r.targetType === 'on_driver');
                                                if (isAdmin && driverReview) {
                                                    return (
                                                        <div className="flex flex-col gap-1 pl-3 border-l border-border/50">
                                                            <StarRating rating={driverReview?.rating || 0} starSize="w-3.5 h-3.5" />
                                                            {driverReview?.comment && (
                                                                <span className="text-xs text-muted-foreground italic">"{driverReview?.comment}"</span>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <DetailItem label="Driver Name" value={order?.driver?.fullName || '---'} icon={<User />} />
                                    <DetailItem label="Phone" value={order?.driver?.phone || '---'} icon={<Phone />} />
                                    {order?.driver?.email && (
                                        <DetailItem label="Email" value={order?.driver?.email} icon={<Mail />} />
                                    )}
                                    {order?.driverStatus && (
                                        <DetailItem
                                            label="Status"
                                            value={
                                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${order?.driverStatus === 'arrived' || order?.driverStatus === 'completed'
                                                    ? 'bg-green-100 text-green-700'
                                                    : 'bg-blue-100 text-blue-700'
                                                    }`}>
                                                    {order?.driverStatus?.replace('_', ' ')}
                                                </span>
                                            }
                                            icon={<Info />}
                                        />
                                    )}

                                    {/* {order.deliveryCode && (
                                        <DetailItem label="Delivery Code" value={<span className="font-mono font-bold tracking-widest">{order.deliveryCode}</span>} icon={<Hash />} />
                                    )} */}
                                    {(() => {
                                        const pickup = order.statusHistories?.find(h => h.status === 'picked_up')?.createdAt;
                                        return pickup ? (
                                            <DetailItem label="Pickup Time" value={formatDateTime(pickup)} icon={<Clock />} />
                                        ) : null;
                                    })()}
                                    {(() => {
                                        const delivered = order.statusHistories?.find(h => h.status === 'delivered')?.createdAt;
                                        return delivered ? (
                                            <DetailItem label="Delivered Time" value={formatDateTime(delivered)} icon={<CheckCircle2 />} />
                                        ) : null;
                                    })()}
                                </div>
                            </SectionCard>
                        )}

                        {/* Driver Settlement (Admin Only) */}
                        {isAdmin && order?.driverEarning && (
                            <SectionCard
                                title="Driver Settlement"
                                icon={<Banknote />}
                            >
                                <p className="text-sm text-muted-foreground mb-4 px-1">This section shows driver payout calculation.</p>
                                <div className="overflow-x-auto rounded-xl border border-secondary/15 bg-secondary/5">
                                    <table className="w-full text-sm text-left">
                                        <thead className="bg-secondary/10 text-muted-foreground font-semibold">
                                            <tr>
                                                <th className="px-4 py-3 border-b w-1/2">Field</th>
                                                <th className="px-4 py-3 border-b w-1/2">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border/50">
                                            <tr className="hover:bg-secondary/10 transition-colors">
                                                <td className="px-4 py-3 text-muted-foreground">Delivery Fee Collected</td>
                                                <td className="px-4 py-3 font-medium">{formatRand(order?.deliveryFee || 0)}</td>
                                            </tr>
                                            <tr className="hover:bg-secondary/10 transition-colors">
                                                <td className="px-4 py-3 text-muted-foreground">Delivery Order Type</td>
                                                <td className="px-4 py-3 font-medium">
                                                    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full capitalize ${!order?.rideId
                                                        ? 'bg-slate-100 text-slate-700'
                                                        : 'bg-purple-100 text-purple-700'
                                                        }`}>
                                                        {!order?.rideId ? 'Single Order' : 'Multiple Order'}
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr className="hover:bg-secondary/10 transition-colors">
                                                <td className="px-4 py-3 text-muted-foreground">Driver Base Earnings</td>
                                                <td className="px-4 py-3 font-medium">{formatRand(order?.driverEarning?.baseFare || 0)}</td>
                                            </tr>
                                            <tr className="hover:bg-secondary/10 transition-colors">
                                                <td className="px-4 py-3 text-muted-foreground">
                                                    Distance Bonus {order?.driverEarning?.distance ? `(${order?.driverEarning?.distance} km)` : ''}
                                                </td>
                                                <td className="px-4 py-3 font-medium">{formatRand(order?.driverEarning?.distanceFare)}</td>
                                            </tr>
                                            {
                                                <tr className="hover:bg-secondary/10 transition-colors">
                                                    <td className="px-4 py-3 text-muted-foreground">Tip Received</td>
                                                    <td className="px-4 py-3 font-medium">{formatRand(Number(order?.driverEarning?.tip))}</td>
                                                </tr>
                                            }
                                            {/* {Number(order.penaltyAmount) > 0 && (
                                                <tr className="hover:bg-secondary/10 transition-colors">
                                                    <td className="px-4 py-3 text-muted-foreground">Driver Penalties</td>
                                                    <td className="px-4 py-3 font-medium text-destructive">-{formatRand(order.penaltyAmount)}</td>
                                                </tr>
                                            )} */}
                                            <tr className="hover:bg-secondary/10 transition-colors bg-secondary/10">
                                                <td className="px-4 py-3 font-bold text-foreground">Driver Total Earnings</td>
                                                <td className="px-4 py-3 font-bold text-primary">{formatRand(order?.driverEarning?.earning)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </SectionCard>
                        )}

                        {/* Order Items */}
                        <SectionCard
                            title="Order Items"
                            icon={<ShoppingBag />}
                            extra={
                                <span className="text-[10px] font-bold py-1 px-3 bg-primary/10 text-primary rounded-full">
                                    {order.items?.length ?? 0} Items
                                </span>
                            }
                        >
                            {order.items && order.items.length > 0 ? (
                                <>
                                    <div className="overflow-x-auto rounded-xl border border-secondary/15 bg-secondary/5">
                                        <table className="w-full text-sm text-left">
                                            <thead className="bg-secondary/10 text-muted-foreground font-semibold">
                                                <tr>
                                                    <th className="px-4 py-3 border-b">Item Details</th>
                                                    <th className="px-4 py-3 border-b text-right">Qty</th>
                                                    <th className="px-4 py-3 border-b text-right">Price</th>
                                                    <th className="px-4 py-3 border-b text-right">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-border/50">
                                                {order.items.map((item, index) => (
                                                    <React.Fragment key={item.id || index}>
                                                        <tr className="hover:bg-secondary/10 transition-colors">
                                                            <td className="px-4 py-3">
                                                                <div className="flex items-start gap-3">
                                                                    <div className="w-10 h-10 rounded-lg bg-secondary/10 flex-shrink-0 flex items-center justify-center overflow-hidden border border-secondary/20">
                                                                        {item?.product?.media?.[0]?.url ? (
                                                                            <img
                                                                                src={API_URL + "/" + item?.product?.media?.[0]?.url}
                                                                                alt={item?.product?.name || 'Product'}
                                                                                className="w-full h-full object-cover"
                                                                                onError={(e) => {
                                                                                    (e.target as HTMLImageElement).style.display = 'none';
                                                                                    (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                                                                                }}
                                                                            />
                                                                        ) : null}
                                                                        <Package className={`w-4 h-4 text-muted-foreground opacity-50 ${item?.product?.media?.[0]?.url ? 'hidden' : ''}`} />
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="flex items-center gap-2">
                                                                            <h4 className="font-semibold text-foreground truncate">
                                                                                {item?.product?.name || 'Product'}
                                                                            </h4>
                                                                            {(() => {
                                                                                const productReview = (order?.reviews || []).find(r => r?.targetType === 'on_product' && r?.targetId === item?.productId);
                                                                                if (productReview) {
                                                                                    return (
                                                                                        <div className="flex flex-col items-start mt-1">
                                                                                            <div className="flex items-center gap-1">
                                                                                                <StarRating rating={productReview?.rating || 0} starSize="w-3 h-3" />
                                                                                            </div>
                                                                                            {productReview?.comment && (
                                                                                                <span className="text-[10px] text-muted-foreground italic mt-0.5 max-w-[150px] truncate" title={productReview?.comment}>
                                                                                                    "{productReview?.comment}"
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    );
                                                                                }
                                                                                return null;
                                                                            })()}
                                                                        </div>
                                                                        {item?.variant && item?.variant?.name && (
                                                                            <p className="text-[11px] font-bold text-primary uppercase tracking-wider mt-0.5">
                                                                                Variant: {item?.variant?.name}
                                                                                {item?.variant?.price ? ` ${formatRand(item?.variant?.price)}` : ''}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3 text-right font-medium">{item?.quantity}</td>
                                                            <td className="px-4 py-3 text-right">
                                                                {(() => {
                                                                    const originalPrice = Number(item?.unitPrice);
                                                                    const discountedAmount = Number(item?.discountedProductAmount);
                                                                    const hasDiscount = discountedAmount > 0 && discountedAmount < originalPrice;
                                                                    const currentPrice = hasDiscount ? item?.totalItemPrice : originalPrice;

                                                                    return hasDiscount ? (
                                                                        <div className="flex flex-col items-end">
                                                                            <span className="text-xs text-muted-foreground line-through">{formatRand(originalPrice * Number(item?.quantity))}</span>
                                                                            <span className="font-medium text-green-600">{formatRand(currentPrice)}</span>
                                                                        </div>
                                                                    ) : (
                                                                        <span>{formatRand(currentPrice)}</span>
                                                                    );
                                                                })()}
                                                            </td>
                                                            <td className="px-4 py-3 text-right font-bold text-primary">
                                                                {(() => {
                                                                    const originalPrice = Number(item?.unitPrice);
                                                                    const discountedAmount = Number(item?.discountedProductAmount);
                                                                    const hasDiscount = discountedAmount > 0 && discountedAmount < originalPrice;
                                                                    const currentPrice = hasDiscount ? item?.totalItemPrice : item?.totalItemPrice;

                                                                    return formatRand(currentPrice);
                                                                })()}
                                                            </td>
                                                        </tr>
                                                        {item.addons && item.addons.length > 0 && item.addons.map((addon) => (
                                                            <tr key={addon.id} className="bg-secondary/5/50">
                                                                <td className="px-4 py-2 pl-12">
                                                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                                        <Tag className="w-3 h-3" />
                                                                        <span>{addon?.addon?.name}</span>
                                                                    </div>
                                                                </td>
                                                                <td className="px-4 py-2 text-right text-xs text-muted-foreground">{addon.quantity}</td>
                                                                <td className="px-4 py-2 text-right text-xs text-muted-foreground">+{formatRand(addon.price)}</td>
                                                                <td className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">+{formatRand(Number(addon.price) * addon.quantity)}</td>
                                                            </tr>
                                                        ))}
                                                    </React.Fragment>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>


                                    <div className="mt-6">
                                        <h4 className="font-bold text-foreground mb-3 px-1 text-base">Bottom Summary</h4>
                                        <div className="overflow-x-auto rounded-xl border border-secondary/15 bg-secondary/5">
                                            <table className="w-full text-sm text-left">
                                                <thead className="bg-secondary/10 text-muted-foreground font-semibold">
                                                    <tr>
                                                        <th className="px-4 py-3 border-b w-1/2">Field</th>
                                                        <th className="px-4 py-3 border-b w-1/2">Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-border/50">
                                                    <tr className="hover:bg-secondary/10 transition-colors">
                                                        <td className="px-4 py-3 text-muted-foreground">Items Total</td>
                                                        <td className="px-4 py-3 font-medium">{formatRand(order.totalAmount)}</td>
                                                    </tr>
                                                    {Number(order.taxAmount) > 0 && (
                                                        <tr className="hover:bg-secondary/10 transition-colors">
                                                            <td className="px-4 py-3 text-muted-foreground">Taxes</td>
                                                            <td className="px-4 py-3 font-medium">{formatRand(order.taxAmount)}</td>
                                                        </tr>
                                                    )}
                                                    {Number(order.plateformFree) > 0 && (
                                                        <tr className="hover:bg-secondary/10 transition-colors">
                                                            <td className="px-4 py-3 text-muted-foreground">Platform Fee</td>
                                                            <td className="px-4 py-3 font-medium">{formatRand(order.plateformFree)}</td>
                                                        </tr>
                                                    )}
                                                    {Number(order.deliveryFee) > 0 && (
                                                        <tr className="hover:bg-secondary/10 transition-colors">
                                                            <td className="px-4 py-3 text-muted-foreground">Packaging / Delivery Charges</td>
                                                            <td className="px-4 py-3 font-medium">{formatRand(order.deliveryFee)}</td>
                                                        </tr>
                                                    )}
                                                    {Number(order.discountAmount) > 0 && (
                                                        <tr className="hover:bg-secondary/10 transition-colors">
                                                            <td className="px-4 py-3 text-muted-foreground">
                                                                Applied Coupon {order?.coupon?.code ? <span className="font-semibold text-primary">({order.coupon.code})</span> : ''}
                                                            </td>
                                                            <td className="px-4 py-3 font-medium">-{formatRand(order.discountAmount)}</td>
                                                        </tr>
                                                    )}
                                                    <tr className="hover:bg-secondary/10 transition-colors bg-secondary/10">
                                                        <td className="px-4 py-3 font-bold text-foreground">Customer Paid</td>
                                                        <td className="px-4 py-3 font-bold text-primary">{formatRand(order.finalAmount)}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Package className="w-10 h-10 mx-auto mb-2 opacity-40" />
                                    <p className="text-sm">No item details available</p>
                                </div>
                            )}
                        </SectionCard>
                        <SectionCard title="Timeline" icon={<Clock />}>
                            <div className="space-y-4">
                                {order.statusHistories?.length ? (
                                    order.statusHistories.slice().sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()).map((h) => (
                                        <DetailItem
                                            key={h.id}
                                            label={h.status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                            value={formatDateTime(h.createdAt)}
                                            icon={<CheckCircle2 className="text-primary w-4 h-4" />}
                                        />
                                    ))
                                ) : (
                                    <>
                                        <DetailItem label="Order Placed" value={formatDateTime(order.createdAt)} icon={<ShoppingBag />} />
                                        <DetailItem label="Last Updated" value={formatDateTime(order.updatedAt)} icon={<Clock />} />
                                    </>
                                )}
                            </div>
                        </SectionCard>
                    </div>

                    {/* RIGHT: sidebar */}
                    <div className="space-y-6">

                        {/* Order Summary */}
                        <SectionCard title="Order Summary" icon={<Info />}>
                            <div className="space-y-3">
                                <DetailItem label="Order ID" value={<span className="font-mono text-primary text-xs">{order?.orderId}</span>} icon={<Hash />} />
                                <DetailItem label="Placed On" value={formatDateTime(order?.createdAt)} icon={<Calendar />} />
                                <DetailItem
                                    label="Payment"
                                    value={
                                        order?.paymentMethod?.toLowerCase() === 'wallet' && order?.pendingPaymentMethod
                                            ? `${order.paymentMethod} + ${order.pendingPaymentMethod}`
                                            : order?.paymentMethod || '---'
                                    }
                                    icon={<Wallet />}
                                />
                                {order?.paymentMethod === "card" && (
                                    <DetailItem label="Transaction ID" value={order?.transaction?.tansactionId || '---'} icon={<Hash />} />
                                )}
                                <DetailItem
                                    label="Pay Status"
                                    value={
                                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${order?.paymentStatus === 'paid'
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-amber-100 text-amber-700'
                                            }`}>
                                            {formatStatus(order?.paymentStatus)}
                                        </span>
                                    }
                                    icon={<CreditCard />}
                                />
                                <DetailItem label="Delivery Type" value={<span className="capitalize">{formatStatus(order?.deliveryType) || '---'}</span>} icon={<Truck />} />
                                <DetailItem
                                    label="Delivery Mode"
                                    value={
                                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${!order?.rideId
                                            ? 'bg-slate-100 text-slate-700'
                                            : 'bg-purple-100 text-purple-700'
                                            }`}>
                                            {!order?.rideId ? 'Single' : 'Multiple'}
                                        </span>
                                    }
                                    icon={<Package />}
                                />
                            </div>

                            {/* <div className="mt-5 pt-4 border-t border-dashed space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Subtotal</span>
                                    <span className="font-medium">{formatRand(order.totalAmount)}</span>
                                </div>
                                {Number(order.discountAmount) > 0 && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Discount</span>
                                        <span className="font-medium text-green-600">−{formatRand(order.discountAmount)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Delivery Fee</span>
                                    <span className="font-medium">{formatRand(order.deliveryFee)}</span>
                                </div>
                                <div className="flex justify-between items-center pt-3 border-t">
                                    <span className="text-sm font-bold uppercase opacity-70">Total</span>
                                    <span className="text-3xl font-black text-primary">{formatRand(order?.finalAmount)}</span>
                                </div>
                            </div> */}
                        </SectionCard>

                        {/* Order Invoices */}
                        <SectionCard title="Order Invoices" icon={<FileText />}>
                            <div className="space-y-3">
                                <p className="text-xs text-muted-foreground leading-relaxed">
                                    Preview official PDF invoices for customer accounting or store settlement records before downloading.
                                </p>
                                <div className="flex flex-col gap-2.5 pt-1">
                                    <Button
                                        variant="outline"
                                        onClick={() => handleViewInvoice('customer')}
                                        isLoading={downloadingInvoice === 'customer'}
                                        disabled={!!downloadingInvoice}
                                        className="w-full justify-between h-10 text-xs font-bold rounded-xl border-secondary/20 hover:bg-primary/5 hover:border-primary/30 transition-all group cursor-pointer"
                                        leftIcon={<FileText className="w-4 h-4 text-primary" />}
                                        rightIcon={<Eye className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors" />}
                                    >
                                        View Customer Invoice
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => handleViewInvoice('store')}
                                        isLoading={downloadingInvoice === 'store'}
                                        disabled={!!downloadingInvoice}
                                        className="w-full justify-between h-10 text-xs font-bold rounded-xl border-secondary/20 hover:bg-primary/5 hover:border-primary/30 transition-all group cursor-pointer"
                                        leftIcon={<Store className="w-4 h-4 text-primary" />}
                                        rightIcon={<Eye className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors" />}
                                    >
                                        View Store Invoice
                                    </Button>
                                </div>
                            </div>
                        </SectionCard>

                        {/* Coupon Details */}
                        {order?.coupon && (
                            <SectionCard title="Coupon Details" icon={<Tag />}>
                                <div className="space-y-3">
                                    <DetailItem label="Coupon Code" value={<span className="font-mono text-primary font-bold uppercase tracking-wider">{order.coupon.code}</span>} icon={<Hash />} />
                                    <DetailItem label="Campaign Name" value={order.coupon.name} icon={<Info />} />
                                    <DetailItem
                                        label="Discount Value"
                                        value={order.coupon.type === 'percentage' ? `${order.coupon.discountValue}%` : formatRand(order.coupon.discountValue)}
                                        icon={<Percent />}
                                    />
                                    {order.coupon.maxDiscount !== null && Number(order.coupon.maxDiscount) > 0 && (
                                        <DetailItem label="Max Discount" value={formatRand(order.coupon.maxDiscount)} icon={<Wallet />} />
                                    )}
                                    <DetailItem label="Min Purchase" value={formatRand(order.coupon.minimumValue || 0)} icon={<ShoppingCart />} />
                                </div>
                            </SectionCard>
                        )}

                        {/* Cancellation Details */}
                        {(order?.status === 'cancelled' || order?.status === 'refund_rejected' || order?.status === 'failed') && (
                            <SectionCard title="Cancellation / Failure Details" icon={<XCircle className="text-destructive w-5 h-5" />}>
                                <div className="space-y-3">
                                    {(order?.cancelledByRole || order?.cancelledBy) && (
                                        <DetailItem
                                            label="Cancelled By"
                                            value={<span className="capitalize">{formatStatus(order?.cancelledBy) || '---'}</span>}
                                            icon={<UserX />}
                                        />
                                    )}
                                    <div className="flex flex-col gap-1">
                                        <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                                            <AlertCircle className="w-3.5 h-3.5" />
                                            Reason
                                        </span>
                                        <p className="text-sm font-medium bg-red-50 text-red-700 p-2.5 rounded-md border border-red-100">
                                            {order?.cancelledReason || 'No reason provided'}
                                        </p>
                                    </div>
                                </div>
                            </SectionCard>
                        )}

                        {/* Store Earnings Summary */}
                        {order.status !== "cancelled" && (
                            <SectionCard title="Store Earnings Summary" icon={<Wallet />}>
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Store Gross Amount</span>
                                        <span className="font-medium">{formatRand(order?.totalAmount)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Store Coupon Discount</span>
                                        <span className="font-medium text-destructive">−{formatRand(order?.storeEarning?.storeDiscountAmount || 0)}</span>
                                    </div>
                                    {order?.storeEarning?.commissionPercentage && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">Commission ({order.storeEarning?.commissionPercentage}%)</span>
                                            <span className="font-medium text-destructive">−{formatRand(order.storeEarning?.commissionAmount || 0)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between items-center pt-3 border-t">
                                        <span className="text-sm font-bold opacity-70">Final Store Earnings</span>
                                        <span className="text-xl font-black text-primary">{formatRand(order.storeEarning?.storePayableAmount)}</span>
                                    </div>
                                </div>
                            </SectionCard>
                        )}

                        {/* Platform Earnings Summary (Admin Only) */}
                        {order.status !== "cancelled" && isAdmin && (
                            <SectionCard title="Platform Earnings Summary" icon={<Banknote />}>
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Store Commission</span>
                                        <span className="font-medium text-green-600">+{formatRand(order?.commissionAmount || 0)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Delivery Fee</span>
                                        <span className="font-medium text-green-600">+{formatRand(order?.deliveryFee || 0)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Platform Fee</span>
                                        <span className="font-medium text-green-600">+{formatRand(order?.plateformFree || 0)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">Tax Amount</span>
                                        <span className="font-medium text-green-600">+{formatRand(order?.taxAmount || 0)}</span>
                                    </div>
                                    {Number(order?.platformDiscountAmount) > 0 && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">Platform Coupon Discount</span>
                                            <span className="font-medium text-destructive">−{formatRand(order?.platformDiscountAmount || 0)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between items-center pt-3 border-t">
                                        <span className="text-sm font-bold opacity-70">Total Collected</span>
                                        <span className="text-xl font-black text-primary">
                                            {formatRand(order.plateformPayableAmount)}
                                        </span>
                                    </div>
                                </div>
                            </SectionCard>
                        )}

                        {/* Store Details — Admin only */}
                        {isAdmin && store && (
                            <SectionCard title="Store Details" icon={<Store />}>
                                <div className="space-y-3">
                                    <DetailItem label="Store Name" value={store?.name} icon={<ChefHat />} />
                                    <DetailItem label="Address" value={store?.address || '---'} icon={<MapPin />} />
                                    <DetailItem label="Contact" value={store?.contact || '---'} icon={<Phone />} />
                                    <DetailItem
                                        label="Status"
                                        value={
                                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full capitalize ${store?.status === 'online'
                                                ? 'bg-green-100 text-green-700'
                                                : store?.status === 'busy'
                                                    ? 'bg-amber-100 text-amber-700'
                                                    : 'bg-red-100 text-red-700'
                                                }`}>
                                                {store?.status}
                                            </span>
                                        }
                                        icon={<Info />}
                                    />
                                    {store?.settings && (
                                        <>
                                            <DetailItem
                                                label="Opening Time"
                                                value={formatTime12Hour(store?.settings?.openingTime)}
                                                icon={<AlarmClock />}
                                            />
                                            <DetailItem
                                                label="Closing Time"
                                                value={formatTime12Hour(store?.settings?.closingTime)}
                                                icon={<Clock />}
                                            />
                                        </>
                                    )}
                                </div>
                            </SectionCard>
                        )}

                        {/* Quick Actions */}
                        {(!isCancelled && !isFailed && canEdit(RESOURCES.ORDER)) && (
                            <SectionCard title="Quick Actions" icon={<CheckCircle2 />}>
                                <div className="space-y-3">
                                    {/* Force Delivery */}
                                    {order.status === 'destination_arrived' && (
                                        <Button
                                            variant="success" fullWidth
                                            leftIcon={<CheckCircle2 className="w-4 h-4" />}
                                            isLoading={actionLoading === 'delivered'}
                                            disabled={Boolean(actionLoading)}
                                            onClick={() => setShowForceDeliveryModal(true)}
                                        >
                                            Force Delivery
                                        </Button>
                                    )}

                                    {/* Store Manager Actions */}
                                    {!isAdmin && (
                                        <>
                                            {order.status === 'pending' && (
                                                <>
                                                    {order?.deliveryType !== 'takeaway' && (
                                                        <div className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-secondary/5 mb-2">
                                                            <div className="space-y-0.5">
                                                                <Label className="text-sm font-medium">Notify Drivers</Label>
                                                                <p className="text-[10px] text-muted-foreground leading-tight">Automatically alert nearby drivers</p>
                                                            </div>
                                                            <Switch
                                                                checked={notifyDriver}
                                                                onCheckedChange={handleNotificationToggle}
                                                                disabled={isToggleLoading}
                                                            />
                                                        </div>
                                                    )}
                                                    <Button
                                                        variant="success" fullWidth
                                                        leftIcon={<CheckCircle2 className="w-4 h-4" />}
                                                        isLoading={actionLoading === 'accepted'}
                                                        disabled={isToggleLoading || Boolean(actionLoading)}
                                                        onClick={() => handleStatusUpdate('accepted')}
                                                    >
                                                        Accept Order
                                                    </Button>
                                                    <Button
                                                        variant="destructive" fullWidth
                                                        leftIcon={<XCircle className="w-4 h-4" />}
                                                        disabled={Boolean(actionLoading) || isToggleLoading}
                                                        isLoading={actionLoading === 'cancelled' || actionLoading === 'refund_rejected'}
                                                        onClick={() => confirmCancel(
                                                            'Reject Order',
                                                            'Are you sure you want to reject this order? The customer will be notified.'
                                                        )}
                                                    >
                                                        Reject Order
                                                    </Button>
                                                </>
                                            )}

                                            {order.status === 'accepted' && (
                                                <>
                                                    <Button
                                                        variant="primary" fullWidth
                                                        leftIcon={<CheckCircle2 className="w-4 h-4" />}
                                                        isLoading={actionLoading === 'ready_for_pickup'}
                                                        disabled={Boolean(actionLoading)}
                                                        onClick={() => handleStatusUpdate('ready_for_pickup')}
                                                    >
                                                        Mark as Ready
                                                    </Button>
                                                    <Button
                                                        variant="outline" fullWidth
                                                        leftIcon={<XCircle className="w-4 h-4" />}
                                                        disabled={Boolean(actionLoading)}
                                                        isLoading={actionLoading === 'cancelled'}
                                                        onClick={() => confirmCancel(
                                                            'Cancel Order',
                                                            'Are you sure you want to cancel this order? This action cannot be undone.'
                                                        )}
                                                    >
                                                        Cancel Order
                                                    </Button>
                                                </>
                                            )}

                                            {order.status === 'ready_for_pickup' && (
                                                <div className="space-y-3 p-4 bg-primary/5 border border-primary/10 rounded-2xl text-center mt-2">
                                                    <label className="text-xs font-semibold text-foreground flex items-center justify-center gap-1.5">
                                                        <Lock className="w-3.5 h-3.5 text-primary" />
                                                        {order?.deliveryType === 'takeaway' ? 'Customer Collection OTP' : 'Driver Delivery Code'}
                                                    </label>
                                                    <p className="text-[10px] text-muted-foreground leading-relaxed">
                                                        {order?.deliveryType === 'takeaway'
                                                            ? 'Ask the customer for their collection OTP and enter it below to confirm delivery.'
                                                            : 'Give this 4-digit code to the driver to verify and confirm pickup.'}
                                                    </p>

                                                    {order?.deliveryType === 'takeaway' ? (
                                                        <div className="space-y-3 mt-2">
                                                            <div className="flex justify-center gap-3 py-2" onPaste={handleTakeawayOtpPaste}>
                                                                {takeawayOtpValues.map((digit, idx) => (
                                                                    <input
                                                                        key={idx}
                                                                        id={`takeaway-otp-${idx}`}
                                                                        type="text"
                                                                        inputMode="numeric"
                                                                        maxLength={1}
                                                                        value={digit}
                                                                        onChange={(e) => handleTakeawayOtpChange(idx, e.target.value)}
                                                                        onKeyDown={(e) => handleTakeawayOtpKeyDown(idx, e)}
                                                                        className="w-12 h-12 text-center text-xl font-bold rounded-xl border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all shadow-sm"
                                                                    />
                                                                ))}
                                                            </div>
                                                            <Button
                                                                variant="primary"
                                                                fullWidth
                                                                size="sm"
                                                                leftIcon={<CheckCircle2 className="w-3.5 h-3.5" />}
                                                                isLoading={actionLoading === 'delivered'}
                                                                disabled={fullTakeawayOtp.length < 4 || Boolean(actionLoading)}
                                                                onClick={() => handleStatusUpdate('delivered', { otp: fullTakeawayOtp })}
                                                            >
                                                                Verify & Mark as Delivered
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <div className="py-2.5 bg-background border border-dashed border-primary/20 rounded-xl">
                                                            <span className="text-2xl font-mono font-black tracking-[0.2em] text-primary">
                                                                {order?.deliveryCode || '----'}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </SectionCard>
                        )}

                        {/* Timeline */}
                    </div>

                </div>

            </div>

            {/* Cancel / Reject Confirmation Modal */}
            <ConfirmationModal
                open={showCancelModal}
                onOpenChange={setShowCancelModal}
                onConfirm={() => handleStatusUpdate(
                    order?.status === 'refund_requested' ? 'refund_rejected' : 'cancelled'
                )}
                title={cancelContext.title}
                message={cancelContext.message}
                confirmText="Yes, Confirm"
                cancelText="No, Go Back"
                loading={Boolean(actionLoading)}
                type="danger"
            >
                <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold text-foreground">Reason (Optional)</label>
                    <textarea
                        className="w-full p-3 rounded-xl border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm resize-none"
                        rows={3}
                        placeholder="Please provide a reason for cancelling..."
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                    />
                </div>
            </ConfirmationModal>

            {/* Force Delivery Confirmation Modal */}
            <ConfirmationModal
                open={showForceDeliveryModal}
                onOpenChange={setShowForceDeliveryModal}
                onConfirm={async () => {
                    await handleStatusUpdate('delivered');
                    setShowForceDeliveryModal(false);
                }}
                title="Force Delivery"
                message="Are you sure you want to force mark this order as delivered? This action will bypass the OTP verification and should only be used when necessary."
                confirmText="Yes, Force Delivery"
                cancelText="Cancel"
                loading={actionLoading === 'delivered'}
                type="warning"
                size='lg'
            />

            {/* Assign Driver Modal */}
            <AssignDriverModal
                open={showAssignModal}
                onOpenChange={setShowAssignModal}
                orderId={order?.id}
                currentDriverId={order?.driverId}
                currentDriver={order?.driver}
                onSuccess={fetchOrder}
            />

            {/* Invoice Viewer Modal */}
            <ViewDocumentModal
                open={invoiceModalOpen}
                onOpenChange={setInvoiceModalOpen}
                documents={invoiceDocItems}
            />
        </Layout >
    );
};
